import classNames from "classnames";
import React from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { ConfigData, publicUrl } from "../config";
import { Messages, MessagesProvider } from "../context/MessagesContext";
import { RadarProvider } from "../context/RadarContext";
import { toQuarter } from "../date";
import { useFetch } from "../hooks/use-fetch";
import { useSearchParamState } from "../hooks/use-search-param-state";
import { Item, filteredOnly, getTags } from "../model";
import { DEFAULT_RADAR, RadarSlug, isRadarSlug, radarDataUrl } from "../radars";
import { itemsAtRelease, latestRelease, resolveRelease } from "../releases";
import Header from "./Header/Header";
import RadarsHome from "./RadarsHome/RadarsHome";
import Router from "./Router";
import SiteFooter from "./SiteFooter/SiteFooter";

const usePage = (params: Record<string, string | undefined>) => {
  return (params["*"] || "").replace(".html", "");
};

type FilteredItems = {
  /** Items of the selected release, tags applied. Drives chart, quadrants, overview. */
  items: Item[];
  /** Items with tags applied only, full revision history. Drives the item pages. */
  allItems: Item[];
  selectedRelease?: string;
};

/**
 * Single place where both the tag filter and the release filter are applied, so
 * Header, Router and Footer always see the same set of items.
 */
const useFilteredItems = ({
  items,
  releases,
}: {
  items: Item[];
  releases: string[];
}): FilteredItems => {
  const [searchParamState] = useSearchParamState();
  const { tags, release } = searchParamState;

  const allItems = tags ? filteredOnly(items, tags) : items;
  const selectedRelease = resolveRelease(releases, release);

  return {
    items: selectedRelease
      ? itemsAtRelease(allItems, selectedRelease, releases)
      : allItems,
    allItems,
    selectedRelease,
  };
};

const RouterWithPageParam = ({
  items,
  releases,
  config,
}: {
  items: Item[];
  releases: string[];
  config: ConfigData;
}) => {
  const page = usePage(useParams());
  const [searchParamState] = useSearchParamState();
  const { search } = searchParamState;
  const {
    items: filteredItems,
    allItems,
    selectedRelease,
  } = useFilteredItems({ items, releases });

  return (
    <Router
      pageName={page || ""}
      search={search || ""}
      items={filteredItems}
      allItems={allItems}
      releases={releases}
      selectedRelease={selectedRelease}
      config={config}
    />
  );
};

const HeaderWithPageParam = ({ items }: { items: Item[] }) => {
  const page = usePage(useParams());
  const tags = getTags(items);

  return <Header pageName={page || ""} tags={tags} />;
};

/** The site footer, told which edition of this radar is the current one. */
const RadarFooter = ({ releases }: { releases: string[] }) => {
  const latest = latestRelease(releases);

  return <SiteFooter latestQuarter={latest ? toQuarter(latest) : undefined} />;
};

interface Data {
  items: Item[];
  releases: string[];
}

const RadarApp = ({ slug }: { slug: RadarSlug }) => {
  const buildHash = process.env.REACT_APP_BUILDHASH;
  const data = useFetch<Data>(radarDataUrl(slug, `rd.json?${buildHash}`));
  const messages = useFetch<Messages>(
    radarDataUrl(slug, `messages.json?${buildHash}`)
  );
  const config = useFetch<ConfigData>(
    radarDataUrl(slug, `config.json?${buildHash}`)
  );

  if (!data || !config) {
    return null;
  }

  const { items, releases } = data;

  return (
    <RadarProvider slug={slug}>
      <MessagesProvider messages={messages}>
        {/*
          Header and footer sit outside `.page`: both are full bleed by design
          and `.page` is a 1200px column. The header positions itself, the page
          only reserves the height.
        */}
        <HeaderWithPageParam items={items} />
        <div className="page">
          <div className={classNames("page__content")}>
            <RouterWithPageParam
              config={config}
              items={items}
              releases={releases}
            />
          </div>
        </div>
        <RadarFooter releases={releases} />
      </MessagesProvider>
    </RadarProvider>
  );
};

/**
 * Resolves `/:radar/*`.
 *
 * Every URL of the pre-split radar (`/index.html`, `/overview.html`,
 * `/software-engineering/Java.html`, ...) captures its first segment as
 * `radar`. `isRadarSlug` rejects it and the whole legacy deep-link space is
 * redirected under `/tech/` in this single spot, which is what preserves the
 * indexed URLs and the SEO.
 */
export const RadarShell = () => {
  const params = useParams();
  const location = useLocation();
  const slug = params.radar || "";
  const rest = params["*"] || "";

  if (!isRadarSlug(slug)) {
    return (
      <Navigate
        replace
        to={{
          pathname: `/${DEFAULT_RADAR}/${slug}${rest ? `/${rest}` : ""}`,
          search: location.search,
        }}
      />
    );
  }

  if (rest === "") {
    return (
      <Navigate
        replace
        to={{ pathname: `/${slug}/index.html`, search: location.search }}
      />
    );
  }

  return <RadarApp slug={slug} />;
};

export default function App() {
  return (
    <BrowserRouter basename={`${publicUrl}`}>
      <Routes>
        <Route path="/" element={<RadarsHome />} />
        <Route path="/:radar/*" element={<RadarShell />} />
      </Routes>
    </BrowserRouter>
  );
}
